import { createContext, useContext, useMemo, useEffect, useRef, useState, useCallback } from 'react'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import type { Query, Mutation } from '@tanstack/react-query'
import { configureWidgetClient, setWidgetCustomer } from '../ofetch-client'
import { getWidgetConfig, listProjects, type WidgetConfig, type ProjectListItem } from '../api-client'
import { loadWidgetConfig, saveWidgetConfig, saveSelectedProject, loadSelectedProject, resolvePreferences } from '../storage'
import { initWidgetAuth, getAuthState, onAuthStateChange, type WidgetAuthState } from '../auth/widget-auth'
import { resolveIdentityMode } from '../auth/identity-resolver'
import { getRegistry } from '../registry'
import { RegistryProvider } from './registry-context'
import { NotificationProvider, pushNotificationExternal } from './notifications'
import { SdkReactBridge } from './sdk-react-adapter'
import { en } from '../i18n/en'
import { ar } from '../i18n/ar'
import type { AuthMode, WidgetError } from '../types'
import type { ReqdeskProviderProps, ReqdeskContextValue, WidgetMutationMeta, WidgetQueryMeta } from './types'

const LOCALE_TABLES: Record<string, Record<string, string>> = { en, ar }

const SAFE_DEFAULT_MODES: AuthMode[] = ['sso', 'email']

const ReqdeskContext = createContext<ReqdeskContextValue | null>(null)

export function useReqdeskContext(): ReqdeskContextValue {
  const ctx = useContext(ReqdeskContext)
  if (!ctx) throw new Error('useReqdesk must be used within a <ReqdeskProvider>')
  return ctx
}

/** Internal singletons so every `<ReqdeskProvider>` mount reuses the same query cache while
 *  still letting tests reset the module with a fresh QueryClient if they import this directly. */

function translate(locale: string | undefined, key: string, translations?: Record<string, string>): string {
  if (translations?.[key]) return translations[key]
  const table = LOCALE_TABLES[locale ?? 'en'] ?? LOCALE_TABLES.en
  return table[key] ?? key
}

/**
 * Build the widget's QueryClient with a `QueryCache` + `MutationCache` that funnels every
 * unhandled error into the notification stack. Per-query / per-mutation opt-outs flow through
 * `meta: WidgetQueryMeta | WidgetMutationMeta`:
 *   - `meta.silent` — suppress the toast entirely.
 *   - `meta.allow404` — skip the 404 toast (probe-style queries).
 *   - `meta.form` — skip the 422 toast (the view renders per-field errors inline via
 *     `<FormErrorProvider>`). Non-422 errors still toast so network / 5xx failures never hide.
 *
 * `translate` is passed in so the cache callbacks can localize copy against the current locale
 * without reaching into a stale module-level snapshot.
 */
function buildWidgetQueryClient(getLocale: () => { locale: string; translations?: Record<string, string> }): QueryClient {
  const notifyFromError = (
    error: unknown,
    meta: WidgetQueryMeta | WidgetMutationMeta | undefined,
    source: 'query' | 'mutation',
  ) => {
    const widgetErr = asWidgetError(error)
    if (shouldSuppressToast(widgetErr, meta, source)) return

    const { locale, translations } = getLocale()
    const t = (k: string) => translate(locale, k, translations)
    const title = widgetErr.message || defaultTitleFor(widgetErr, t)
    const detail = widgetErr.errors && widgetErr.errors.length > 0
      ? widgetErr.errors
        .filter(fe => !fe.field) // only surface unmapped field errors here; per-field live in the form
        .map(fe => fe.detail || fe.code)
        .filter(Boolean)
      : undefined

    pushNotificationExternal({
      kind: 'error',
      title,
      detail,
      autoDismissMs: null, // errors stay until dismissed
    })
  }

  const queryCache = new QueryCache({
    onError: (error: unknown, query: Query) => notifyFromError(error, query.meta as WidgetQueryMeta | undefined, 'query'),
  } as unknown as ConstructorParameters<typeof QueryCache>[0])

  const mutationCache = new MutationCache({
    onError: (error: unknown, _vars: unknown, _ctx: unknown, mutation: Mutation) =>
      notifyFromError(error, mutation.meta as WidgetMutationMeta | undefined, 'mutation'),
  } as unknown as ConstructorParameters<typeof MutationCache>[0])

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: (failureCount, error) => {
          const status = (error as { status?: number })?.status
          if (status && status >= 400 && status < 500 && status !== 408) return false
          if (status && status >= 500) return false
          return failureCount < 2
        },
        retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  })
}

function asWidgetError(error: unknown): WidgetError {
  if (error instanceof Error && 'code' in error) {
    // WidgetFetchError (and any future typed subclass) — already shaped.
    return error as unknown as WidgetError
  }
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    return error as WidgetError
  }
  return {
    code: 'UNKNOWN',
    message: error instanceof Error ? error.message : 'Something went wrong.',
  }
}

function shouldSuppressToast(
  err: WidgetError,
  meta: WidgetQueryMeta | WidgetMutationMeta | undefined,
  source: 'query' | 'mutation',
): boolean {
  if (!meta) return false
  if (meta.silent) return true
  if (source === 'query') {
    const qmeta = meta as WidgetQueryMeta
    if (qmeta.allow404 && err.status === 404) return true
  }
  if (source === 'mutation') {
    const mmeta = meta as WidgetMutationMeta
    // When the view declares a form binding, 422 renders inline per-field. Other statuses still
    // toast so the user learns about network / 5xx failures even when per-field UX is present.
    if (mmeta.form && err.status === 422) return true
  }
  return false
}

function defaultTitleFor(err: WidgetError, t: (k: string) => string): string {
  if (!err.status) return t('error.network')
  if (err.status === 401) return t('error.unauthenticated')
  if (err.status === 403) return t('error.forbidden')
  if (err.status === 404) return t('error.notFound')
  if (err.status >= 500) return t('error.server')
  if (err.status === 422) return t('error.validation')
  return t('error.generic')
}

// Module-level singleton keyed on QueryClient. The locale getter is re-bound on each provider
// mount so `translate()` sees the up-to-date language without re-creating the QueryClient (which
// would blow away in-flight queries).
let sharedQueryClient: QueryClient | null = null
const localeGetterRef: { current: () => { locale: string; translations?: Record<string, string> } } = {
  current: () => ({ locale: 'en', translations: undefined }),
}

function getWidgetQueryClient(): QueryClient {
  if (!sharedQueryClient) {
    sharedQueryClient = buildWidgetQueryClient(() => localeGetterRef.current())
  }
  return sharedQueryClient
}

export function ReqdeskProvider({
  apiKey,
  apiUrl,
  auth,
  theme,
  language,
  customer,
  translations,
  authMode,
  display,
  actions,
  initialPreferences,
  userPreferences,
  onPreferencesChange,
  menuCloseOnAction,
  hideFab,
  hideDisplayModePicker,
  fabIcon,
  children,
}: ReqdeskProviderProps) {
  const initialized = useRef(false)
  const [authState, setAuthState] = useState<WidgetAuthState>(getAuthState)
  const [serverConfig, setServerConfig] = useState<WidgetConfig | null>(null)
  const isWorkspaceKey = apiKey.startsWith('rqd_ws_')

  // Project selection state (multi-project support for OIDC users)
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [isProjectLoading, setIsProjectLoading] = useState(false)

  const saved = initialized.current ? null : loadWidgetConfig(apiKey)
  const resolvedLanguage = language ?? saved?.language ?? 'en'
  const resolvedTheme = theme ?? saved?.theme

  // Seed the framework-agnostic registry with host-supplied config. This must run before the
  // widget opens so preferences and actions are in place for the first render.
  const registryInitialized = useRef(false)
  if (!registryInitialized.current) {
    const registry = getRegistry()
    registry.setApiKeyForStorage(apiKey)
    registry.setInitialPreferences(initialPreferences ?? {})
    registry.setConfigDisplay(display)
    registry.setMenuCloseOnAction(menuCloseOnAction ?? true)
    registry.setHostPreferencesCallback(onPreferencesChange)
    const resolvedPrefs = resolvePreferences({
      apiKey,
      initialPreferences,
      userPreferences,
    })
    registry.initPreferencesSnapshot(resolvedPrefs)
    if (actions) {
      // ReactMenuActionInput widens `icon` to include ReactNode; the registry is framework-
      // agnostic and stores the action as-is. The React renderer path (<Trigger>, menu views)
      // resolves ReactNode icons correctly; vanilla paths ignore non-serializable icons.
      for (const action of actions) registry.addAction(action as unknown as import('../types').MenuActionInput)
    }
    registryInitialized.current = true
  }

  // Propagate dynamic prop changes into the registry.
  useEffect(() => {
    const registry = getRegistry()
    registry.setHostPreferencesCallback(onPreferencesChange)
  }, [onPreferencesChange])

  useEffect(() => {
    const registry = getRegistry()
    registry.setConfigDisplay(display)
  }, [display])

  useEffect(() => {
    const registry = getRegistry()
    registry.setMenuCloseOnAction(menuCloseOnAction ?? true)
  }, [menuCloseOnAction])

  useEffect(() => {
    if (!initialized.current) {
      configureWidgetClient(apiUrl || window.location.origin, apiKey)
      setWidgetCustomer(customer ?? null)
      initialized.current = true

      // Workspace keys can't resolve _current — we fetch widget-config per selected project instead
      if (!isWorkspaceKey) {
        getWidgetConfig('_current')
          .then(config => {
            setServerConfig(config)

            // Init auth from server config if no local auth prop provided
            if (!auth && config.authIssuerUri && config.authClientId) {
              initWidgetAuth({
                issuerUri: config.authIssuerUri,
                clientId: config.authClientId,
              })
            }
          })
          .catch(() => {
            // Config fetch failed — widget continues with local props/defaults
          })
      }

      // Init auth from local props immediately if provided
      if (auth) {
        initWidgetAuth(auth)
      }
    }
  }, [apiKey, apiUrl, auth, isWorkspaceKey])

  useEffect(() => {
    // Re-sync state on subscribe — initWidgetAuth() runs in a parallel effect
    // and may have already updated module-level state before this listener attached.
    setAuthState(getAuthState())
    return onAuthStateChange(setAuthState)
  }, [])

  // Propagate customer changes into the HTTP client so signed-identity headers stay in sync.
  useEffect(() => {
    setWidgetCustomer(customer ?? null)
  }, [customer])

  useEffect(() => {
    saveWidgetConfig(apiKey, {
      language: resolvedLanguage,
      theme: resolvedTheme,
    })
  }, [apiKey, resolvedLanguage, resolvedTheme])

  // Fetch projects when authenticated. Workspace keys require OIDC login —
  // the widget will show a login prompt until the user is signed in.
  useEffect(() => {
    if (!authState.isAuthenticated) {
      // Not logged in yet:
      //  - Workspace key → stay idle, user must log in to pick a project
      //  - Project key  → use _current (rewrites to the key's project)
      setSelectedProjectId(isWorkspaceKey ? null : '_current')
      setProjects([])
      setIsProjectLoading(false)
      return
    }

    setIsProjectLoading(true)
    listProjects(serverConfig?.workspaceId ?? undefined)
      .then(projectList => {
        setProjects(projectList)

        const userKey = authState.userEmail ?? 'anon'
        const saved = loadSelectedProject(apiKey, userKey)

        // Use saved selection if it's still in the list
        if (saved && projectList.some(p => p.id === saved)) {
          setSelectedProjectId(saved)
        }
        // Auto-select the API key's own project (from widget-config ID, OIDC only)
        else if (serverConfig?.id && projectList.some(p => p.id === serverConfig.id)) {
          setSelectedProjectId(serverConfig.id)
          saveSelectedProject(apiKey, userKey, serverConfig.id)
        }
        // Single project — auto-select
        else if (projectList.length === 1) {
          setSelectedProjectId(projectList[0].id)
          saveSelectedProject(apiKey, userKey, projectList[0].id)
        }
        // Multiple with no saved/default — null triggers picker
        else if (projectList.length > 1) {
          setSelectedProjectId(null)
        }
        // No projects available
        else {
          setSelectedProjectId(isWorkspaceKey ? null : '_current')
        }
      })
      .catch(() => {
        // Fallback: workspace keys can't use _current, leave null so picker/error shows
        setSelectedProjectId(isWorkspaceKey ? null : '_current')
      })
      .finally(() => setIsProjectLoading(false))
  }, [authState.isAuthenticated, serverConfig, apiKey, authState.userEmail, isWorkspaceKey])

  const selectProject = useCallback((id: string) => {
    setSelectedProjectId(id)
    const userKey = authState.userEmail ?? 'anon'
    saveSelectedProject(apiKey, userKey, id)
  }, [apiKey, authState.userEmail])

  // Merge: local props > server config > defaults
  const mergedTheme = useMemo(() => {
    const base = { ...(resolvedTheme ?? {}) } as Record<string, unknown>
    if (serverConfig) {
      if (!base.brandName) base.brandName = serverConfig.brandName || serverConfig.projectName
      if (!base.logo && serverConfig.logoUrl) base.logo = serverConfig.logoUrl
    }
    return base as ReqdeskProviderProps['theme']
  }, [resolvedTheme, serverConfig])

  const mergedAuth = auth ?? (serverConfig?.authIssuerUri && serverConfig?.authClientId
    ? { issuerUri: serverConfig.authIssuerUri, clientId: serverConfig.authClientId }
    : undefined)

  // Prefer a concrete project id whenever we know one — the widget-config endpoint accepts the
  // "_current" alias (it's resolved by the API key's origin server-side), but not every ticket
  // endpoint does. Once `getWidgetConfig('_current')` returns the resolved `id`, use it for
  // subsequent calls so unauthenticated / signed-identity hosts don't 404 on
  // `/projects/_current/tickets`. Falls back to the alias only until serverConfig arrives.
  const resolvedProjectId = selectedProjectId ?? serverConfig?.id ?? '_current'
  const isMultiProject = authState.isAuthenticated && projects.length > 1

  const serverAllowedModes: AuthMode[] = useMemo(() => {
    const raw = serverConfig?.allowedAuthModes
    if (!Array.isArray(raw) || raw.length === 0) return SAFE_DEFAULT_MODES
    return raw.filter((m): m is AuthMode => m === 'sso' || m === 'email' || m === 'signed')
  }, [serverConfig?.allowedAuthModes])

  const resolvedIdentityMode = useMemo(
    () => resolveIdentityMode({
      customer,
      serverAllowed: serverAllowedModes,
      override: authMode,
      isSsoAuthenticated: authState.isAuthenticated,
    }),
    [customer, serverAllowedModes, authMode, authState.isAuthenticated],
  )

  const value = useMemo<ReqdeskContextValue>(() => ({
    apiKey,
    auth: mergedAuth,
    theme: mergedTheme,
    language: resolvedLanguage,
    customer,
    translations,
    isAuthenticated: authState.isAuthenticated,
    userEmail: authState.userEmail,
    userName: authState.userName,
    showAllTickets: serverConfig?.showAllTickets ?? false,
    projectId: resolvedProjectId,
    projects,
    isMultiProject,
    selectProject,
    isProjectLoading,
    serverAllowedModes,
    resolvedIdentityMode,
    hideFab,
    hideDisplayModePicker,
    fabIcon,
  }), [apiKey, mergedAuth, mergedTheme, resolvedLanguage, customer, translations, authState, serverConfig, resolvedProjectId, projects, isMultiProject, selectProject, isProjectLoading, serverAllowedModes, resolvedIdentityMode, hideFab, hideDisplayModePicker, fabIcon])

  // Seed the registry with dynamic context (locale, user, projectId) so ActionContext handlers
  // receive up-to-date values.
  useEffect(() => {
    const registry = getRegistry()
    registry.setContext({
      user: customer ? { email: customer.email, name: customer.name } : null,
      projectId: resolvedProjectId,
      locale: resolvedLanguage,
    })
  }, [customer, resolvedProjectId, resolvedLanguage])

  // Keep the module-level locale getter in sync so the QueryCache / MutationCache error
  // handlers localize their toast copy against the current language + translations prop. We
  // avoid re-creating the QueryClient here (would blow away in-flight queries) — the getter
  // closure is re-read on every notification.
  useEffect(() => {
    localeGetterRef.current = () => ({ locale: resolvedLanguage, translations })
  }, [resolvedLanguage, translations])

  const queryClient = getWidgetQueryClient()

  return (
    <ReqdeskContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <RegistryProvider>
            <SdkReactBridge>{children}</SdkReactBridge>
          </RegistryProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </ReqdeskContext.Provider>
  )
}
